import { LitElement, type ReactiveController, type ReactiveControllerHost } from 'lit';
import { customElement } from 'lit/decorators.js';
import { consume, ContextConsumer } from '@lit/context';

import { ButtressDbService, buttressDbServiceContext } from '@buttress/crag';

import { nsTrustScoreServiceContext } from './contexts.js';
import { ButtressSubscriptions } from '../helpers/buttress-subscriptions.js';
import {
  loadedTrustScoreDocId,
  toPublicTrustScore,
  type PublicTrustScore,
  type TrustScoreRow,
} from '../helpers/trust-score-display.js';
import { createLogger } from './logger.js';

const logger = createLogger('trust-score-service');

// Ids per $in query, and so the query's limit
const CHUNK_SIZE = 200;
// Dev Buttress delivers each change once per socket worker, so one change arrives many times in a burst
const CHANGE_DEBOUNCE_MS = 300;

export const TRUST_SCORE_CHANGE = 'trust-score-change';

export interface TrustScoreChangeDetail {
  companyIds: string[];
  // loaded: a first read answered; changed: a cached score changed; failed: the read failed, so the scores stay unknown
  reason: 'loaded' | 'changed' | 'failed';
}

/**
 * The published trust scores of the companies on screen, shared by every surface (.ai/client-structure.md).
 * Requests made in the same tick go out as one `companyTrustScore` `$in` query per 200 ids, and each answer is cached for the
 * session. One subscription on the collection keeps the cache live: crag applies each change to its store, and the service
 * reads the changed row from there. A row crag hasn't seen, such as a newly published or suppressed company, crag loads
 * silently after its `dataservice:loadById` event, so no subscriber hears it: the service reads that row itself. There is no
 * re-read on focus or mount. Fires `trust-score-change` with the company ids whose score arrived, changed or failed to load.
 */
@customElement('trust-score-service')
export class NSTrustScoreService extends LitElement {
  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  private readonly __subscriptions = new ButtressSubscriptions(this, () => this.__db);

  // companyId -> its row, or null when the read returned none (nothing published)
  private readonly __rows = new Map<string, TrustScoreRow | null>();

  private readonly __companyByDocId = new Map<string, string>();

  private readonly __inFlight = new Map<string, Promise<void>>();

  private __queued = new Set<string>();

  private __flushScheduled = false;

  private __flushPromise: Promise<void> = Promise.resolve();

  private __changedDocIds = new Set<string>();

  private __rescanAll = false;

  private __changeTimer?: number;

  // The db service element listened on for rows crag loads itself
  private __loadByIdTarget?: EventTarget;

  private readonly __loadingDocIds = new Set<string>();

  connectedCallback(): void {
    super.connectedCallback();
    // The subscription ends on disconnect, so cached scores are followed again on reconnect
    if (this.__rows.size > 0) this.__follow();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.clearTimeout(this.__changeTimer);
    this.__loadByIdTarget?.removeEventListener('dataservice:loadById', this.__onLoadById);
    this.__loadByIdTarget = undefined;
  }

  /** The company's public score: undefined while it loads (or was never requested), otherwise none, suppressed or scored. */
  get(companyId: string): PublicTrustScore | undefined {
    if (!this.__rows.has(companyId)) return undefined;
    return toPublicTrustScore(this.__rows.get(companyId));
  }

  /** Asks for the companies' scores without waiting; a `trust-score-change` says when they arrive. */
  request(companyIds: Iterable<string>): void {
    void this.load(companyIds);
  }

  /** Reads the companies' scores not already cached or on their way, and resolves once every one of them is known. */
  load(companyIds: Iterable<string>): Promise<void> {
    const waits: Promise<void>[] = [];
    for (const companyId of new Set(companyIds)) {
      if (!companyId || this.__rows.has(companyId)) continue;
      const inFlight = this.__inFlight.get(companyId);
      if (inFlight) {
        waits.push(inFlight);
        continue;
      }
      this.__queued.add(companyId);
    }
    if (this.__queued.size > 0) waits.push(this.__scheduleFlush());
    return Promise.all(waits).then(() => undefined);
  }

  // Requests made in the same tick share one query
  private __scheduleFlush(): Promise<void> {
    if (this.__flushScheduled) return this.__flushPromise;
    this.__flushScheduled = true;
    this.__flushPromise = Promise.resolve().then(() => {
      this.__flushScheduled = false;
      const ids = [...this.__queued];
      this.__queued = new Set();
      const chunks: Promise<void>[] = [];
      for (let start = 0; start < ids.length; start += CHUNK_SIZE) {
        const chunk = ids.slice(start, start + CHUNK_SIZE);
        const read = this.__read(chunk).finally(() => chunk.forEach((id) => this.__inFlight.delete(id)));
        chunk.forEach((id) => this.__inFlight.set(id, read));
        chunks.push(read);
      }
      return Promise.all(chunks).then(() => undefined);
    });
    return this.__flushPromise;
  }

  private async __read(companyIds: string[]): Promise<void> {
    const db = this.__db;
    if (!db) {
      logger.warn('No db service, so no trust scores were read');
      this.__notify(companyIds, 'failed');
      return;
    }
    this.__follow();
    try {
      const result = await db.query(
        'companyTrustScore',
        { companyId: { $in: companyIds } },
        { limit: companyIds.length },
      );
      const rows = (result?.results ?? []) as unknown as TrustScoreRow[];
      companyIds.forEach((companyId) => this.__rows.set(companyId, null));
      rows.forEach((row) => this.__store(row));
      this.__notify(companyIds, 'loaded');
    } catch (err) {
      // Left uncached, so the next request tries again
      logger.error('Unable to read trust scores', err);
      this.__notify(companyIds, 'failed');
    }
  }

  private __store(row: TrustScoreRow) {
    if (!row.companyId) return;
    if (row.id) this.__companyByDocId.set(row.id, row.companyId);
    this.__rows.set(row.companyId, row);
  }

  private __follow() {
    const db = this.__db;
    if (db && this.__loadByIdTarget !== db) {
      this.__loadByIdTarget?.removeEventListener('dataservice:loadById', this.__onLoadById);
      db.addEventListener('dataservice:loadById', this.__onLoadById);
      this.__loadByIdTarget = db;
    }
    if (this.__subscriptions.active) return;
    this.__subscriptions.subscribe('companyTrustScore.*', (cr: { path?: string }) => this.__changed(cr));
  }

  // A row that has just become readable: crag loads it silently, so it is read here too and applied as a change
  private readonly __onLoadById = (event: Event) => {
    const docId = loadedTrustScoreDocId((event as CustomEvent).detail);
    const db = this.__db;
    if (!docId || !db || this.__rows.size < 1 || this.__loadingDocIds.has(docId)) return;
    this.__loadingDocIds.add(docId);
    db.getById<TrustScoreRow & { id: string }>('companyTrustScore', docId)
      .then((row) => {
        if (row) this.__apply([row]);
      })
      .catch((err) => logger.error(`Unable to read trust score ${docId}`, err))
      .finally(() => this.__loadingDocIds.delete(docId));
  };

  private __changed(cr: { path?: string }) {
    const docId = cr?.path?.split('.')[1];
    if (docId) this.__changedDocIds.add(docId);
    else this.__rescanAll = true;
    window.clearTimeout(this.__changeTimer);
    this.__changeTimer = window.setTimeout(() => this.__applyChanges(), CHANGE_DEBOUNCE_MS);
  }

  // Reads each changed row from crag's store
  private __applyChanges() {
    const db = this.__db;
    const docIds = [...this.__changedDocIds];
    const rescan = this.__rescanAll;
    this.__changedDocIds = new Set();
    this.__rescanAll = false;
    if (!db) return;

    const rows: TrustScoreRow[] = rescan
      ? [...((db.get('companyTrustScore') as Map<string, TrustScoreRow> | undefined)?.values() ?? [])]
      : docIds
          .map((docId) => (db.get(`companyTrustScore.${docId}`) as TrustScoreRow | undefined) ?? undefined)
          .filter((row): row is TrustScoreRow => !!row);
    this.__apply(rows);
  }

  // Stores the rows of companies a surface asked for and says which changed; any other company is ignored
  private __apply(rows: TrustScoreRow[]) {
    const changed = new Set<string>();
    rows.forEach((row) => {
      const companyId = row.companyId ?? (row.id ? this.__companyByDocId.get(row.id) : undefined);
      if (!companyId || !this.__rows.has(companyId)) return;
      this.__store({ ...row, companyId });
      changed.add(companyId);
    });
    if (changed.size > 0) this.__notify([...changed], 'changed');
  }

  private __notify(companyIds: string[], reason: TrustScoreChangeDetail['reason']) {
    this.dispatchEvent(new CustomEvent<TrustScoreChangeDetail>(TRUST_SCORE_CHANGE, { detail: { companyIds, reason } }));
  }
}

// The parts of the service its consumers use, so the gallery can provide a stub
export type TrustScoreSource = Pick<
  NSTrustScoreService,
  'get' | 'request' | 'load' | 'addEventListener' | 'removeEventListener'
>;

/**
 * Follows one company's public trust score for a host: reads it through the trust-score service and re-renders the host when
 * it changes. With `lazy`, nothing is read until `request()` is called (the badge reads when its popover opens); otherwise it is
 * read whenever the host updates with a new company id.
 */
export class TrustScoreController implements ReactiveController {
  private readonly __host: ReactiveControllerHost & HTMLElement;

  private readonly __companyId: () => string | null | undefined;

  private readonly __lazy: boolean;

  private __service?: TrustScoreSource;

  private __requestedId?: string;

  private __wanted = false;

  // The company whose read failed: not read again until `request()` asks, or a non-lazy host is shown again
  private __failedId?: string;

  constructor(
    host: ReactiveControllerHost & HTMLElement,
    companyId: () => string | null | undefined,
    { lazy = false }: { lazy?: boolean } = {},
  ) {
    this.__host = host;
    this.__companyId = companyId;
    this.__lazy = lazy;
    host.addController(this);
    // Registered after this controller, so hostConnected attaches the listener once the service has arrived
    new ContextConsumer(host, {
      context: nsTrustScoreServiceContext,
      callback: (service) => this.__setService(service),
    });
  }

  /** The score, undefined while it loads or when there is no company or service. */
  get value(): PublicTrustScore | undefined {
    const companyId = this.__companyId();
    return companyId ? this.__service?.get(companyId) : undefined;
  }

  /** Whether the last read of the score failed; the next `request()` tries again. */
  get failed(): boolean {
    const companyId = this.__companyId();
    return !!companyId && companyId === this.__failedId && this.value === undefined;
  }

  /** Reads the score now, for a lazy controller, and again after a failed read. */
  request(): void {
    this.__wanted = true;
    this.__failedId = undefined;
    this.__requestCurrent();
  }

  hostConnected(): void {
    this.__service?.addEventListener(TRUST_SCORE_CHANGE, this.__onChange);
    // A host that reads on its own (the feed's light) has no request() to retry a failed read with: it tries again each time
    // it is shown, rather than reading as unavailable for the rest of the session
    if (!this.__lazy && this.__failedId) {
      this.__failedId = undefined;
      this.__requestedId = undefined;
      this.__host.requestUpdate();
    }
  }

  hostDisconnected(): void {
    this.__service?.removeEventListener(TRUST_SCORE_CHANGE, this.__onChange);
  }

  hostUpdate(): void {
    if (!this.__lazy || this.__wanted) this.__requestCurrent();
  }

  private __setService(service: TrustScoreSource) {
    if (service === this.__service) return;
    this.__service?.removeEventListener(TRUST_SCORE_CHANGE, this.__onChange);
    this.__service = service;
    this.__requestedId = undefined;
    this.__failedId = undefined;
    if (this.__host.isConnected) service.addEventListener(TRUST_SCORE_CHANGE, this.__onChange);
    this.__host.requestUpdate();
  }

  private __requestCurrent() {
    const companyId = this.__companyId();
    if (!companyId || !this.__service || companyId === this.__requestedId || companyId === this.__failedId) return;
    this.__requestedId = companyId;
    this.__service.request([companyId]);
  }

  private readonly __onChange = (event: Event) => {
    const companyId = this.__companyId();
    const detail = (event as CustomEvent<TrustScoreChangeDetail>).detail;
    if (!companyId || !detail?.companyIds.includes(companyId)) return;
    if (detail.reason === 'failed' && this.__service?.get(companyId) === undefined) {
      this.__failedId = companyId;
      this.__requestedId = undefined;
    }
    this.__host.requestUpdate();
  };
}
